import type { ColorKey } from '@iheartjane/reefer';
import {
  BlossomIcon,
  HopIcon,
  LeafIcon,
  LemonIcon,
  PepperIcon,
  SoilIcon,
} from '@iheartjane/reefer';

import type { Terpene } from '@jane/bloom/types/schemas/marketplace/terpenes';

export const TERPENE_DETAILS: Partial<Record<Terpene, string>> = {
  bisabolol: `Bisabolol is an herbal terpene commonly found in German chamomile. The aroma is floral and sweet with similar citrus flavors and a chamomile finish. Bisabolol can help with inflammation and is commonly found in cannabis cultivars like Green Crack, Strawberry Cough and Durban Poison.`,
  borneol: `Borneol is a terpene that smells like pine and wood found in cannabis and many other plants. Chinese herbalists have utilized the therapeutic effects of Borneol to promote activity in the central nervous system.`,
  caryophyllene: `Caryophyllene smells like black pepper and musk. The taste can be spicy, specifically peppery. Caryophyllene can produces beneficial effects for muscle relief, sleeplessness, and nervousness. Caryophyllene is commonly found in black pepper.`,
  eucalyptol: `Eucalyptol is the dominant terpene found in eucalyptus oil and can demonstrate potential powerful antiinflammatory effects by promoting healthy repiratory function. Eucalyptol is found in eucalyptus but also in bay leaves and at times the aroma can resemble a strong mint with a spicy cooling effect.`,
  humulene: `Humulene smells like earthy hops, with a woody and spicy flavor. It is commonly found in basil, coriander, and hops. Humulene can help with pain management.`,
  limonene: `Limonene is the second most common terpene in nature and is abundant in citrus. The aroma and flavor is that of citrus fruit. This is a powerful terpene that can promote relaxation with its uplifting and mood enhancing qualities. Many studies confirm that limonene can also assist with nervousness, plus can be an incredible antioxidant.`,
  linalool: `Linalool is abundant in lavender and has a very floral aroma. In studies linalool can produce sedative effects when inhaled and can be perfect monoterpene pairing for rest, sleep, nervousness and muscle pain. Linalool is found in lavender cannabis, Bergamot and other cultivars like OG Kush, L.A. Confidential, Black Mamba, and Purple family of strains like Mendo Purps and Purple Urkle.`,
  myrcene: `Myrcene is a signature terpene found in fresh cannabis, hops and lemongrass. The effects can be relaxing and can assist in inducing sleep. Myrcene in cannabis is associated with the “couch lock” effect and can be detected by its herbal and earthy aroma, plus woody and spicy flavor. Myrcene is present in Kush genetics, Cookies, and Chemdawg.`,
  nerolidol: `Nerolidol is a cannabis sesquiterpene and is found in citrus peels. The potential therapeutic effects of Nerolidol include ease of penetration through the skin. Evidence of potential antifungal properties and antioxidant properties are strong with Nerolidol and for those needing rest Nerolidol can be a natural sedative.`,
  ocimene: `Ocimene is a special terpene associated with fruits like strawberries, mangos, and herbs like mint and basil. The aromas are sweet, herbal, sometimes sparking memories of sacred woods and incense. Flavors of Ocimene are sweet with a slight citrus essence in some plants. Ocimene is covetted in creating perfumes due to its sweet scent. It can promote healthy nasal breathing, while also serving as a natural antibacterial and antifungal agent.`,
  pinene: `Pinene is the most abundant terpene in nature. Commonly rich in evergreen trees and botanical essential oils serving plants by repelling insects with their profound pine aromas. Pinene is found in pine, rosemary and parsley. The effects can promote breathing and aid in mild memory problems.`,
  terpinolene: `Terpinolene is an earthy terpene found in pine, lilac and nutmeg. The aroma is floral with a woody citrus edge. Cannabis cultivars will develop different spectrums of terpenes to express their unique terroirs in various geographical regions. The presence of terpinolene is one indicator to distinguish unique cultivars from all over the world. Terpinolene can be a strong antioxidant and can produce sedative effects plus can have effective transdermal applications.`,
};

export const TERPENE_FLAVORS: Record<Terpene, string> = {
  bisabolol: 'herbal',
  borneol: 'herbal',
  camphene: 'earthy',
  camphor: 'earthy',
  carene: 'earthy',
  caryophyllene: 'spicy',
  'caryophyllene oxide': 'spicy',
  cedrene: 'earthy',
  cymene: 'herbal',
  eucalyptol: 'earthy',
  fenchol: 'herbal',
  geraniol: 'floral',
  'geranyl acetate': 'floral',
  guaiol: 'earthy',
  humulene: 'hoppy',
  isoborneol: 'herbal',
  isopulegol: 'herbal',
  limonene: 'citrusy',
  linalool: 'floral',
  menthol: 'herbal',
  myrcene: 'hoppy',
  nerolidol: 'citrusy',
  ocimene: 'herbal',
  phellandrene: 'earthy',
  phytol: 'herbal',
  pinene: 'earthy',
  pulegone: 'herbal',
  sabinene: 'spicy',
  terpineol: 'earthy',
  terpinolene: 'earthy',
  valencene: 'citrusy',
};

/* eslint-disable perfectionist/sort-objects */
export const TERPENE_COLORS: Record<Terpene, ColorKey> = {
  limonene: 'gold',
  nerolidol: 'gold',
  valencene: 'gold',
  caryophyllene: 'ember-dark',
  'caryophyllene oxide': 'ember-dark',
  sabinene: 'ember-dark',
  linalool: 'pacific',
  geraniol: 'pacific',
  'geranyl acetate': 'pacific',
  humulene: 'success',
  myrcene: 'success',
  pinene: 'tangerine-dark',
  camphene: 'tangerine-dark',
  camphor: 'tangerine-dark',
  carene: 'tangerine-dark',
  cedrene: 'tangerine-dark',
  guaiol: 'tangerine-dark',
  eucalyptol: 'tangerine-dark',
  terpineol: 'tangerine-dark',
  terpinolene: 'tangerine-dark',
  phellandrene: 'tangerine-dark',
  bisabolol: 'emerald',
  borneol: 'emerald',
  cymene: 'emerald',
  fenchol: 'emerald',
  menthol: 'emerald',
  isoborneol: 'emerald',
  isopulegol: 'emerald',
  pulegone: 'emerald',
  ocimene: 'emerald',
  phytol: 'emerald',
};
/* eslint-enable perfectionist/sort-objects */

export const FLAVOR_ICONS = {
  citrusy: LemonIcon,
  earthy: SoilIcon,
  floral: BlossomIcon,
  herbal: LeafIcon,
  hoppy: HopIcon,
  spicy: PepperIcon,
};
